<template>
    <div id="document-list" v-if="headers.length > 0">
        <div id="document-list-sticky-info">
            <!------------------------------------------------>
            <div id="document-list-top-options" ref="fixed_header_content" class="clearfix p-b-10">
                <div class="check-option pull-left">
                    <label class="control-label m-b-0 m-r-30"
                        :class="outliersOnly ? 'text-muted' : 'cursor-default'"
                        :style="outliersOnly ? { cursor: 'not-allowed', opacity: '0.6' } : {}"
                        @click="!outliersOnly && toggleInProgress()"
                        v-if="selectedDocTypeCodes.indexOf('report') !== -1">
                        <i class="fa fa-check-square-o" v-if="showInProgress"></i>
                        <i class="fa fa-square-o" v-else></i>
                        Incomplete Reports
                    </label>

                    <!-- Outliers only -->
                    <label class="control-label cursor-default m-b-0 m-r-30"
                        v-if="selectedDocTypeCodes.indexOf('report') !== -1 && ev_outliers_enabled"
                        @click="toggleOutliersOnly">
                        <i class="fa" :class="outliersOnly ? 'fa-check-square-o' : 'fa-square-o'"></i>
                        Outliers only
                        <i class="fa fa-info-circle m-l-5" data-toggle="tooltip" data-placement="top"
                            title="Only available for VERT reports. Excludes in-progress reports."></i>
                    </label>

                    <document-column-settings-modal v-if="document_type" :headers="headers"
                        :show_balance_column="showBalanceColumn" @save-settings="onColumnSettingsSaved" />
                </div>

                <div class="pull-right" v-if="selectedDocTypeCodes.indexOf('report') !== -1">
                    <button type="button" class="btn btn-lg btn-primary m-r-5" @click="showReportFormatModal()"
                        :disabled="selectedEmailableReports.length === 0" :title="elementReportWarning">
                        <i class="fa fa-envelope-o"></i>
                        E-Mail Reports
                    </button>

                    <button type="button" class="btn btn-lg btn-primary" @click="goToGrid"
                        :disabled="viewResultsDisabled" :title="viewResultsDisabledText">
                        <i class="fa fa-table"></i>
                        View Results
                    </button>
                </div>

                <div class="pull-right"
                    v-else-if="selectedDocTypeCodes.indexOf('invoice') !== -1 && selectableRows.length > 0">
                    <button type="button" class="btn btn-lg btn-success" :disabled="selectedRows.length === 0"
                        @click="paySelectedInvoices($event)" style="width:230px">
                        <i class="fa fa-credit-card"></i>
                        Pay Selected Invoices
                    </button>
                </div>

                <!-- invisible blank button to keep spacing uniform on other pages -->
                <div class="pull-right invisible" v-else>
                    <button type="button" class="btn btn-lg">&nbsp;</button>
                </div>

                <div class="pull-right m-r-10" v-if="selectedToPay.length > 0">
                    <button type="button" class="btn btn-lg btn-success" :disabled="selectedToPay.length === 0"
                        @click="paySelectedReports($event)" style="width:225px">
                        <i class="fa fa-credit-card"></i>
                        Pay Selected Reports
                    </button>
                </div>
            </div>
            <!------------------------------------------------------------------>
            <!-- STICKY HEADER -->
            <table class="table table-condensed">
                <thead>
                    <tr>
                        <th class="action-buttons">
                            <span class="hidden-xs expander" @click.stop="toggleDetails()"
                                v-if="table_headers.line2.length > 0 && expandableItems.length > 0">
                                <i class="fa fa-caret-down" v-if="all_expanded"></i>
                                <i class="fa fa-caret-right" v-else></i>
                            </span>

                            <span class="selector" @click="toggleSelection()" v-if="selectableRows.length > 0">
                                <i class="fa fa-check-square" v-if="selectAllChecked"></i>
                                <i class="fa fa-square-o" v-else></i>
                            </span>
                            <span v-else>&nbsp;</span>
                        </th>
                        <th class="hidden visible-xs"></th>
                        <th class="hidden visible-xs mobile-buttons"></th>
                        <th class="hidden-xs p-t-15 p-b-15" :class="header.column" v-for="header in table_headers.line1"
                            v-show="displayColumn(header.column)">
                            <table-column-filter :column_name="header.column" :label="header.label"
                                :sort_method="header.sort_method" :rows="statelessRowData"
                                :active_filters="activeFilters" :visible_rows="visibleRows" :active_sort="activeSort"
                                @sort="sortRows" @add_filter="addNewFilter" @remove_filter="removeFilter"
                                @clear_filters="removeColumnFilters" v-if="header.show_label">
                            </table-column-filter>
                        </th>
                    </tr>
                </thead>
            </table>
        </div>
        <!------------------------------------------------------------------>
        <!-- VISIBLE DOCUMENT LIST TABLE -->
        <div id="document-list-table" class="container-fluid raised low">
            <table class="table table-condensed" :class="{ 'table-hover': table_headers.line2.length > 0 }">
                <thead style="visibility:hidden">
                    <tr>
                        <th class="action-buttons p-t-15 p-b-15">
                            <span class="hidden-xs expander"
                                v-if="table_headers.line2.length > 0 && expandableItems.length > 0">
                                <i class="fa fa-caret-right"></i>
                            </span>

                            <span class="selector" v-if="selectableRows.length > 0">
                                <i class="fa fa-square-o"></i>
                            </span>
                        </th>
                        <th class="hidden visible-xs"></th>
                        <th class="hidden visible-xs mobile-buttons"></th>
                        <th class="hidden-xs p-t-15 p-b-15" :class="header.column" v-for="header in table_headers.line1"
                            v-show="displayColumn(header.column)">
                            <div style="position:relative">
                                <span style="padding-right:35px">{{ header.label }}</span>
                                <span class="action-icons">
                                    <i class="fa fa-caret-down"></i>
                                    <i class="fa fa-filter"></i>
                                </span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <!--<tbody is="transition-group" name="table-row">-->
                <tbody>
                    <template v-for="(row, index) in documents">
                        <!-- VISIBLE ROW -->
                        <tr @click.stop="expandOrSelect(row, index)" :key="index + '-0'">
                            <td class="action-buttons">
                                <span class="expander hidden-xs" @click.stop="toggleDetails(index)"
                                    v-if="table_headers.line2.length > 0 && hasExtraDetails(row)">
                                    <i class="fa fa-caret-down" v-if="row.expanded"></i>
                                    <i class="fa fa-caret-right" v-else></i>
                                </span>

                                <span class="selector" v-if="row.selectable">
                                    <i class="fa fa-check-square" v-if="row.selected"></i>
                                    <i class="fa fa-square-o" v-else></i>
                                </span>
                            </td>
                            <!--------------------------------------------------------------------------->
                            <!-- DISPLAYED ON MOBILE DEVICES -->
                            <!--------------------------------------------------------------------------->
                            <td class="hidden visible-xs">
                                <div class="row p-t-5 p-b-5" v-for="(item, item_index) in table_headers.line1"
                                    v-if="!item.column.endsWith('_btn') && displayColumn(item.column)">
                                    <div class="col-xs-4 text-bold">
                                        <span v-if="item.show_label">{{ item.label }}:</span>
                                    </div>
                                    <div class="col-xs-8">
                                        <document-item :column="item" :data="row"
                                            @show_partial_results="showPartialResults($event)"
                                            @download_report="showReportFormatModal($event)">
                                        </document-item>
                                    </div>
                                </div>
                                <div class="row p-t-5 p-b-5" v-for="(item, item_index) in table_headers.line2"
                                    v-if="!item.column.endsWith('_btn') && displayColumn(item.column)">
                                    <div class="col-xs-4 text-bold">
                                        <span v-if="item.show_label">{{ item.label }}:</span>
                                    </div>
                                    <div class="col-xs-8">
                                        <document-item :column="item" :data="row"
                                            @show_partial_results="showPartialResults($event)"
                                            @download_report="showReportFormatModal($event)">
                                        </document-item>
                                    </div>
                                </div>
                            </td>
                            <td class="hidden visible-xs mobile-buttons">
                                <div class="col-xs-12 p-t-5 p-b-5" v-for="(item, item_index) in table_headers.line1"
                                    v-if="item.column.endsWith('_btn') && displayColumn(item.column)">
                                    <document-item :column="item" :data="row"
                                        @show_partial_results="showPartialResults($event)"
                                        @download_report="showReportFormatModal($event)">
                                    </document-item>
                                </div>
                                <div class="col-xs-12 p-t-5 p-b-5" v-for="(item, item_index) in table_headers.line2"
                                    v-if="item.column.endsWith('_btn') && displayColumn(item.column)">
                                    <document-item :column="item" :data="row" btn_class="btn-pdf"
                                        @show_partial_results="showPartialResults($event)"
                                        @download_report="showReportFormatModal($event)">
                                    </document-item>
                                </div>
                            </td>
                            <!--------------------------------------------------------------------------->
                            <!--------------------------------------------------------------------------->
                            <template v-for="(item, item_index) in table_headers.line1">
                                <td class="hidden-xs" :class="item.column" v-if="row.data && item.column in row.data"
                                    v-show="displayColumn(item.column)">
                                    <document-item :column="item" :data="row"
                                        @show_partial_results="showPartialResults($event)"
                                        @download_report="showReportFormatModal($event)">
                                    </document-item>
                                </td>
                                <td class="hidden-xs" :class="item.column" v-else></td>
                            </template>
                        </tr>
                        <!-- HIDDEN DETAILS -->
                        <tr class="extra-details hidden-xs" v-if="table_headers.line2.length > 0" v-show="row.expanded"
                            :key="index + '-1'">
                            <td></td>
                            <td :colspan="detailsColspan">
                                <table class="table table-condensed">
                                    <thead>
                                        <tr>
                                            <th v-for="header in table_headers.line2">
                                                <div v-if="header.show_label">
                                                    {{ header.label }}
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <template v-for="(item, item_index) in table_headers.line2">
                                                <td :class="item.column" v-if="row.data && item.column in row.data">
                                                    <document-item btn_class="btn-sm btn-pdf" :column="item" :data="row"
                                                        @show_partial_results="showPartialResults($event)"
                                                        @download_report="showReportFormatModal($event)">
                                                    </document-item>
                                                </td>
                                                <td :class="item.column" v-else></td>
                                            </template>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <!-- DOWNLOAD REPORTS MODAL MODAL -->
        <div ref="reportFormatModal" class="modal fade" role="dialog" tabindex="-1">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <i class="fa fa-close close" data-dismiss="modal" aria-label="Close" aria-hidden="true"></i>
                        <h4 class="modal-title" v-if="report_format_action === 'download'">Download Report</h4>
                        <h4 class="modal-title" v-else-if="report_format_action === 'email'">
                            E-Mail Report{{ selectedEmailableReports.length > 1 ? 's' : '' }}
                        </h4>
                    </div>

                    <div class="modal-body container-fluid">
                        <div class="p-t-25 p-l-25 p-r-25 font-bold">
                            Please select a format for your download
                        </div>
                        <div class="p-l-25 p-r-25 p-t-10">
                            <div class="p-5" v-for="(report_formats, report_type) in available_report_formats">
                                <label style="text-transform: capitalize;">{{ report_type }} Report Format</label>
                                <select class="form-control" v-model="selected_report_formats[report_type]">
                                    <option disabled :value="null"></option>
                                    <option :value="report_format.format_number"
                                        v-for="report_format in report_formats">
                                        Format {{ report_format.format_number }}: {{ report_format.common_name }}
                                    </option>
                                </select>
                            </div>

                            <div class="p-5" v-show="report_format_action === 'email'">
                                <label>E-Mail To</label>
                                <textarea class="form-control" v-model="email_reports_to"></textarea>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn btn-lg btn-secondary" data-dismiss="modal">
                            <i class="fa fa-times"></i>
                            Close
                        </button>
                        <button type="button" class="btn btn-lg btn-primary" @click="downloadReport"
                            :disabled="!ready_to_download" v-if="report_format_action === 'download'">
                            <i class="fa fa-download"></i>
                            Download
                        </button>
                        <button type="button" class="btn btn-lg btn-primary" @click="emailSelectedReports"
                            :disabled="!ready_to_download" v-else-if="report_format_action === 'email'">
                            <i class="fa fa-envelope-o"></i>
                            E-Mail
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@import 'resources/assets/sass/global_variables';

.table-row-enter,
.table-row-leave-to {
    opacity: 0;
}

.table-row-enter-active {
    transition: all 0.5s ease;
}

.table-row-leave-active {
    transition: all 0.25s ease;
}

.action-icons {
    white-space: nowrap;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);

    .fa-filter {
        zoom: 1.25;
    }
}
</style>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as Vue from "vue";
import * as _ from "lodash";

const MAX_SAMPLE_COUNT = 1500;
const DEFAULT_HEADERS = {
    report: [
        { column: 'account_nbr', label: 'Account', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'date_reported', label: 'Date Reported', show_label: true, sort_method: 'date', line: 1 },
        { column: 'report_nbr', label: 'Report Number', show_label: true, sort_method: 'mwl_report', line: 1 },
        { column: 'orderNumber', label: 'Order #', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'info1', label: 'Info 1', show_label: true, line: 1 },
        { column: 'info2', label: 'Info 2', show_label: true, line: 1 },
        { column: 'info3', label: 'Info 3', show_label: true, line: 2 },
        { column: 'info4', label: 'Info 4', show_label: true, line: 2 },
        { column: 'info5', label: 'Info 5', show_label: true, line: 2 },
        { column: 'balance', label: 'Balance', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'report_btn', label: 'Report', show_label: false, line: 1 },
        { column: 'report_type', label: 'Type', show_label: true, line: 2 },
        { column: 'sample_id', label: 'Sample ID', show_label: true, line: 2 },
        { column: 'sample_count', label: 'Sample Count', show_label: true, line: 2 },
        { column: 'order_form_btn', label: 'Order Form', show_label: false, line: 2 },
        { column: 'billing_summary_btn', label: 'Billing Summary', show_label: false, line: 2 },
        // { column: 'other_report_formats', label: 'Other', show_label: false, line: 1 }
    ],
    invoice: [
        { column: 'id', label: 'ID', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'account_nbr', label: 'Account', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'invoice_date', label: 'Invoice Date', show_label: true, sort_method: 'date', line: 1 },
        { column: 'due_date', label: 'Due Date', show_label: true, sort_method: 'date', line: 1 },
        { column: 'balance', label: 'Balance', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'pay_invoice_btn', label: 'Pay Invoice', show_label: false, line: 1 },
        { column: 'invoice_btn', label: 'Invoice', show_label: false, line: 1 }
    ],
    sampleOrder: [
        { column: 'id', label: 'ID', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'account_nbr', label: 'Account', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'order_date', label: 'Date Ordered', show_label: true, sort_method: 'date', line: 1 },
        { column: 'received_date', label: 'Date Received', show_label: true, sort_method: 'date', line: 1 },
        { column: 'ordered_by', label: 'Ordered By', show_label: true, line: 1 },
        { column: 'samples', label: 'Samples', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'info1', label: 'Info 1', show_label: true, line: 1 },
        { column: 'info2', label: 'Info 2', show_label: true, line: 1 },
        { column: 'order_form_btn', label: 'Order Form', show_label: false, line: 1 }
    ],
    supplyOrder: [
        { column: 'id', label: 'ID', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'account_nbr', label: 'Account', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'order_date', label: 'Date Ordered', show_label: true, sort_method: 'date', line: 1 },
        { column: 'ordered_by', label: 'Ordered By', show_label: true, line: 1 },
        { column: 'supplies', label: 'Supplies', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'total', label: 'Total', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'tracking_btn', label: 'Tracking', show_label: false, line: 1 },
        { column: 'ship_to', label: 'Ship To', show_label: true, line: 2 },
        { column: 'order_form_btn', label: 'Order Form', show_label: false, line: 1 }
    ],
    safetyDataSheet: [
        { column: 'account_nbr', label: 'Account', show_label: true, sort_method: 'numeric', line: 1 },
        { column: 'fileDescription', label: 'Comments', show_label: true, line: 1 },
        { column: 'fileName', label: 'File', show_label: true, line: 1 },
        { column: 'sds_btn', label: 'Download', show_label: false, line: 1 }
    ]
}

export default {
    props: {
        document_type: String,
        documents: { default: function () { return [] } },
        accounts: { default: function () { return [] } },
        all_expanded: { default: false },
        ev_outliers_enabled: { type: Boolean, default: false }
    },
    data: function () {
        return {
            available_report_formats: {},
            selected_report_formats: {},
            safety_data_sheet_for_upload: {},
            reports_to_download: [],
            report_format_action: null,
            email_reports_to: null,
            headers: {}
        }
    },
    computed: {
        ...mapState('documents', [
            'rows',
            'statelessRowData',
            'docTypes',
            'showBalanceColumn',
            'showInProgress',
            'userAccountRoles',
            'selectAllChecked',
            'userIsSysAdmin',
            'activeFilters',
            'activeSort',
            'reportFormats',
            'outliersOnly',
        ]),

        ...mapState('user', [
            'visible_user'
        ]),

        ...mapGetters('documents', [
            'selectedForGridView',
            'availableReportTypes',
            'totalBalanceDue',
            'selectedDocTypeCodes',
            'selectableRows',
            'selectedRows',
            'visibleRows',
            'availableReportFormats'
        ]),

        /**
         * Return all the select rows that are not element, and therefore can be emailed/formatted.
         * @returns {*}
         */
        selectedEmailableReports() {
            return this.selectedForGridView.filter(row => !row.details.element);
        },

        /**
         * If some selected rows are element reports and therefore can't be formatted,
         * return an error message indicated this.  Else, return empty string.
         * @returns {string}
         */
        elementReportWarning() {
            return this.selectedForGridView.some(row => row.details.element === true) ?
                "Some reports you have selected can only be downloaded individually as PDF" :
                "";
        },

        emails_for_reports() {
            if (this.email_reports_to) {
                return this.email_reports_to.replace(/\s+/g, '').replace(',', ';').split(';');
            }

            return [];
        },

        report_emails_valid() {
            if (this.emails_for_reports.length > 0) {
                let emails_are_valid = true;
                const valid_email = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

                this.emails_for_reports.forEach(email => {
                    if (!valid_email.test(String(email))) {
                        emails_are_valid = false;
                    }
                });

                return emails_are_valid;
            }

            return false;
        },

        visibleColumns() {
            return this.table_headers.line1.map(col => { return col.column })
        },

        detailsColspan() {
            let colspan = this.table_headers.line1.length;
            if (!this.showBalanceColumn && this.visibleColumns.includes('balance')) {
                colspan--;
            }
            return colspan;
        },

        expandableItems() {
            return this.documents.filter(item => item.expandable);
        },

        selectedToPay() {
            return this.selectedRows.filter(row => row.needsPayment);
        },

        table_headers() {
            return {
                line1: this.headers.filter(col => col.line === 1),
                line2: this.headers.filter(col => col.line === 2),
                line3: this.headers.filter(col => col.line === 3)
            }
        },

        ready_to_download() {
            if (this.report_format_action === 'email' && !this.report_emails_valid) {
                return false;
            }

            const blank_report_formats = Object.keys(this.selected_report_formats)
                .filter(report_type => !this.selected_report_formats[report_type]);
            return blank_report_formats.length === 0;
        },

        // Total Number of Samples found in all Selected Documents
        // This would only make sense for report documents
        sumSelectedSampleCounts() {
            return this.selectedForGridView.reduce((sum, doc) => {
                if (_.has(doc, ['details', 'sampleCount'])) {
                    sum += parseInt(doc.details.sampleCount);
                }
                return sum;
            }, 0);
        },

        viewResultsDisabled() {
            return this.selectedForGridView.length === 0 || this.maxSampleCountExceeded();
        },

        // Hover text for View Results if they button isn't highlighted
        viewResultsDisabledText() {
            if (this.viewResultsDisabled) {
                if (this.maxSampleCountExceeded()) {
                    return `You have selected reports with too many samples ${this.sumSelectedSampleCounts} / ${this.maxSampleCount()}. Unselect some reports to reduce total number of samples.`;
                } else if (this.selectedForGridView.length === 0) {
                    return "Select reports to view their results.";
                }
            }

            return "";
        }
    },

    methods: {
        ...mapActions('documents', [
            'getReportFormats',
            'toggleBalanceColumn',
            'toggleInProgress',
            'setItemValue',
            'sortRows',
            'removeFilter',
            'addFilter',
            'removeColumnFilters',
            'emailReports',
            'setOutliersOnly',
            'searchUpdated',
        ]),

        onColumnSettingsSaved(column_settings) {
            this.headers = JSON.parse(JSON.stringify(column_settings));

            const new_settings = this.headers.map(col => {
                return { column: col.column, line: col.line }
            });

            this.$http.post('/user/preferences', {
                preferences: [{
                    key: 'document.settings.' + this.document_type,
                    value: new_settings
                }]
            }).then(function (response) {
                if (response && response.status) {
                    $(this.$refs.columSettingsModal).modal('hide');
                }
            });
        },

        loadSettings() {
            if (!this.document_type) return;

            const setting_name = 'document.settings.' + this.document_type;

            this.$http.get('/user/preferences', {
                params: {
                    keys: [setting_name]
                }
            }).then((response) => {
                if (_.has(response, ['data', 'preferences', setting_name])) {
                    let saved_settings = response.data.preferences[setting_name];

                    if (saved_settings) {
                        const default_settings = _.has(DEFAULT_HEADERS, this.document_type)
                            ? DEFAULT_HEADERS[this.document_type]
                            : [];
                        saved_settings = saved_settings.map(col => {
                            let col_defaults = default_settings.find(col2 => col2.column === col.column);
                            return col_defaults ? Object.assign(col_defaults, col) : col;
                        });

                        // Remove saved columns that have been removed
                        let settings = saved_settings.filter(col => default_settings.find(setting => setting.column === col.column));

                        // Add any newly added columns
                        const missing = default_settings.filter(setting => !saved_settings.find(col => col.column === setting.column));
                        settings = settings.concat(missing);

                        this.headers = settings;

                        return;
                    }
                }

                this.headers = DEFAULT_HEADERS[this.document_type];
            },
                (error) => {
                    this.headers = DEFAULT_HEADERS[this.document_type];
                });
        },

        addNewFilter(info) {
            this.$emit('reset_page');
            this.addFilter(info);
        },

        displayColumn(column) {
            if (['balance'].includes(column)) {
                return this.showBalanceColumn;
            }

            return true;
        },

        hasExtraDetails(item) {
            let details = this.table_headers.line2.filter(col => _.has(item, 'data', col.column) && item.data[col.column]
                && (!Array.isArray(item.data[col.column]) || item.data[col.column].length > 0));
            this.setItemValue({ item, key: 'expandable', value: details.length > 0 });
            return details.length > 0;
        },

        goToOldPortal(event) {
            if (event && event.target && event.target.getAttribute('url')) {
                this.$emit('go_to_old_portal', event.target.getAttribute('url'));
            }
        },

        toggleSelection: function (index) {
            this.$emit('toggle_selection', index);
        },

        toggleDetails(index) {
            this.$emit('toggle_details', index);
        },

        goToGrid() {
            this.$emit('change_view', 'results');
        },

        queueSearch() {
            this.$emit('search');
        },

        showPartialResults(id) {
            this.$emit('show_partial_results', id);
        },

        showReportFormatModal(report) {
            this.report_format_action = report ? 'download' : 'email';

            let reports = [];

            if (report) {
                if (report.report_nbr && report.report_type && report.account_nbr) {
                    reports.push(report);
                }
            } else {
                reports = this.selectedRows
                    .filter(row => !row.details.element)
                    .map(report => {
                        return {
                            account_nbr: report.data.account_nbr,
                            report_nbr: report.data.report_nbr,
                            report_type: report.data.report_type
                        }
                    });
            }

            this.reports_to_download = _.groupBy(reports, 'report_type');
            const account_nbrs = _.uniqWith(reports.map(report => report.account_nbr, _.isEqual)).sort();

            this.selected_report_formats = {};
            this.available_report_formats = {};   // reset here to ensure modal doesn't start with the old formats

            // Get the available report types for the selected accounts
            const currentAccountFormats = Object.keys(this.reportFormats).map((account_nbr) => parseInt(account_nbr)).sort();  // All accounts we've already pulled formats for
            const newAccountNbrs = _.difference(account_nbrs, currentAccountFormats);

            // For accounts we don't have formats for yet, do api hit to call
            // The API, once complete, triggers the watch:reportFormats, which calls updateAvailableReportFormats
            newAccountNbrs.forEach(account_nbr => {
                this.getReportFormats(account_nbr);
            });

            // If there are no new accounts, simply reconfigure available_report_formats to reflect report types
            if (newAccountNbrs.length === 0) {
                this.updateAvailableReportFormats();
            }

            $(this.$refs.reportFormatModal).modal('show');
        },

        // Update the report formats in the dropdown menus on the modal
        updateAvailableReportFormats() {
            // Get map of report type to all accounts that requested a report with that report type
            // reportTypeAccountMap = { 'Plant' => [12912], 'Other' => {12912, 18312] }
            const reportTypeAccountMap = Object.entries(this.reports_to_download)
                .reduce((map, [report_type, reports]) => {
                    map[report_type] = _.uniq(reports.map(report => report.account_nbr));
                    return map;
                }, {});

            // Get the formats we should provide in the dropdown
            // Foreach report type, for each account requesting that report type, create a shared format list
            this.available_report_formats = Object.entries(reportTypeAccountMap)
                .reduce((reportFormatMap, [report_type, account_nbrs]) => {
                    reportFormatMap[report_type] = account_nbrs
                        .map(account_nbr => this.availableReportFormats(report_type.toLowerCase(), account_nbr))    // Get Array of Array of Each Account's formats
                        .filter(account_formats => account_formats.length > 0)    // remove accounts with no available formats
                        // Combine all formats into one array only containing formats all accounts share
                        .reduce((all_formats, account_formats) =>
                            // Ignore first, empty array
                            (all_formats.length > 0) ?
                                _.intersectionBy(all_formats, account_formats, 'format_number') :
                                account_formats
                            ,
                            []    // start with empty to avoid TypeError
                        )
                        .sort((formatA, formatB) => formatA.format_number - formatB.format_number);    // Sort by Format number, descending

                    return reportFormatMap;
                }, {}
                );
        },

        downloadReport() {
            Object.keys(this.reports_to_download).forEach(report_type => {
                const format = this.selected_report_formats[report_type];

                this.reports_to_download[report_type].forEach(report => {
                    if (report.report_nbr && report.account_nbr) {
                        const url = '/reports/' + format + '/' + report.report_nbr + '/' + report.account_nbr;
                        mwl.submitHiddenForm(url, {}, "GET", "mylab_download");
                    }
                });
            });
        },

        emailSelectedReports() {
            if (!this.report_emails_valid) {
                toastr.error('Please enter 1 or more valid email separated by a semicolon.', 'Error!');
                return;
            }

            const items = Object.keys(this.selected_report_formats).map(report_type => {
                return {
                    emails: this.emails_for_reports,
                    report_type,
                    report_format: this.selected_report_formats[report_type],
                    reports: this.selectedRows
                        .filter(report => report.data.report_type.toLowerCase() === report_type.toLowerCase())
                        .filter(report => report.data.report_nbr)    // Make sure the selectedRows actually have report numbers
                        .map(report => {
                            return {
                                account_nbr: report.data.account_nbr,
                                report_nbr: report.data.report_nbr
                            }
                        })
                };
            });

            this.emailReports({ items });

            $(this.$refs.reportFormatModal).modal('hide');
        },

        expandOrSelect(row, index) {
            if (row.selectable) {
                this.toggleSelection(index);
            }
            else {
                this.toggleDetails(index);
            }
        },

        paySelectedInvoices(event) {
            $(event.target).button('loading');

            if (this.selectedDocTypeCodes.includes('invoice') && this.selectedRows.length > 0) {
                const invoice_type = _.has(this.selectedRows[0], ['data', 'invoice_type'])
                    ? this.selectedRows[0].data.invoice_type
                    : null;

                if (invoice_type === 'report') {
                    this.paySelectedReportsFromInvoiceScreen();
                } else {
                    const items = this.selectedRows.map(invoice => {
                        return {
                            id: invoice.id.number,
                            balance: invoice.details.balance,
                            account_nbr: invoice.details.account
                        }
                    });
                    mwl.submitHiddenForm('/documents/invoices/pay', {
                        invoices: items.map(invoice => { return invoice.id.number }),
                        items: JSON.stringify(items),
                        price: items.map(invoice => {
                            return parseFloat(invoice.balance)
                        }).reduce((sum, value) => { return sum + value }, 0).toFixed(2)
                    });
                }
            }
        },

        paySelectedReports(event) {
            $(event.target).button('loading');

            if (this.selectedDocTypeCodes.includes('report') && this.selectedToPay.length > 0) {
                const items = this.selectedToPay.map(report => {
                    return { id: report.id, account: report.account, details: report.details }
                });
                mwl.submitHiddenForm('/documents/reports/pay', {
                    reports: items.map(report => { return report.id.number }),
                    items: JSON.stringify(items),
                    price: items.map(report => {
                        return parseFloat(report.details.balanceDue)
                    }).reduce((sum, value) => { return sum + value }, 0).toFixed(2)
                });
            }
        },

        paySelectedReportsFromInvoiceScreen() {
            const items = this.rows.filter(row => row.selected && row.details.balance > 0)
                .map(report => {
                    return {
                        id: report.id,
                        account: report.account,
                        details: Object.assign(report.details, { balanceDue: report.details.balance })
                    }
                });
            mwl.submitHiddenForm('/documents/reports/pay', {
                reports: items.map(report => { return report.id.number }),
                items: JSON.stringify(items),
                price: items.map(report => {
                    return parseFloat(report.details.balanceDue)
                }).reduce((sum, value) => { return sum + value }, 0).toFixed(2)
            });
        },

        affixStickyInfo() {
            const $navContent = $('#nav-content');
            $("#document-list-sticky-info").affix({
                offset: {
                    top: () => {
                        return $("#document-list").offset().top + $navContent.scrollTop() - $navContent.offset().top
                    }
                }
            });
        },

        // We want to still allow users with one giant report to continue onto the results page
        // even if that individual report contains more samples than we would normally allow
        maxSampleCount() {
            const highestSampleCountInSelection = Math.max(...this.selectedForGridView.map(doc => {
                if (_.has(doc, ['details', 'sampleCount'])) {
                    return parseInt(doc.details.sampleCount);
                }
                return 0;
            }));
            return Math.max(MAX_SAMPLE_COUNT, highestSampleCountInSelection);
        },

        maxSampleCountExceeded() {
            return this.sumSelectedSampleCounts > this.maxSampleCount();
        },

        toggleOutliersOnly() {
            const next = !this.outliersOnly;
            this.setOutliersOnly(next);

            if (next && this.showInProgress) {
                this.toggleInProgress();
            } else if (!next && !this.showInProgress) {
                this.toggleInProgress();
            }

            this.searchUpdated(true);
            this.$emit('reset_page');
            this.$emit('search');
        },
    },

    watch: {
        headers(newval, oldval) {
            if (oldval.length === 0 && newval.length > 0) {
                Vue.nextTick(() => {
                    this.affixStickyInfo();
                    $('[data-toggle="tooltip"]').tooltip();
                });
            }
        },

        reportFormats() {
            this.updateAvailableReportFormats();
        }
    },

    mounted() {
        this.email_reports_to = this.visible_user.email;
        this.loadSettings();
        this.$nextTick(() => {
            $('[data-toggle="tooltip"]').tooltip();
        });
    }
}
</script>



// WEBPACK FOOTER //
// document-list.vue?b06867fe